import { cn } from '@/lib/cn';

/**
 * Uma faixa repartida — as partes de um todo, com a legenda trazendo o número de cada
 * uma. Serve para a conferência do painel (confirmadas, ajustadas, ausências, pendentes)
 * e para a situação das frentes na estrutura (planejadas, em execução, concluídas).
 *
 * Cor nunca é o único sinal: a legenda escreve o número e o nome de cada parte, e o
 * `aria-label` da faixa repete tudo por extenso.
 */
export interface Segment {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly tone: 'ok' | 'info' | 'warn' | 'neutral';
}

const SEGMENT_FILL: Readonly<Record<Segment['tone'], string>> = {
  ok: 'var(--color-state-ok)',
  info: 'var(--color-state-info)',
  warn: 'var(--color-state-warn)',
  neutral: 'var(--color-border-subtle)',
};

export function SegmentedBar({
  title,
  segments,
  className,
}: {
  title: string;
  segments: readonly Segment[];
  className?: string;
}) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        className="flex h-2.5 w-full gap-px overflow-hidden rounded-sm bg-(--color-surface-muted)"
        role="img"
        aria-label={`${title}: ${segments
          .map((segment) => `${String(segment.value)} ${segment.label}`)
          .join(', ')}.`}
      >
        {total > 0 &&
          segments
            .filter((segment) => segment.value > 0)
            .map((segment) => (
              <div
                key={segment.key}
                className="h-full"
                style={{
                  width: `${String((segment.value / total) * 100)}%`,
                  backgroundColor: SEGMENT_FILL[segment.tone],
                }}
              />
            ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {segments.map((segment) => (
          <li key={segment.key} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ backgroundColor: SEGMENT_FILL[segment.tone] }}
            />
            <span className="tabular font-medium">{segment.value}</span>
            <span className="text-(--color-content-muted)">{segment.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
